import { Box, CircularProgress, Typography } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import DetailPageHeader from "../components/common/DetailPageHeader";
import SuratJalanTable from "../components/suratJalan/SuratJalanTable";
import { TestId } from "../constants/testIds";
import type { AppDispatch, RootState } from "../store";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import { searchSuratJalan } from "../store/features/suratJalanSlice";
import type translations from "../translations/translations.json";
import type { PaginationParam } from "../types/paginationResponse";
import type { SuratJalanSearchFilters } from "../types/suratJalan";

type TranslationKeys = keyof typeof translations.en;

const DEFAULT_PAGE_SIZE = 10;

const SuratJalanListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { suratJalans, loading, error, pagination } = useSelector(
    (state: RootState) => state.suratJalan,
  );
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);
  const user = useSelector((state: RootState) => state.user.user);
  const isActionRequired = location.pathname === "/surat-jalan/action-required";

  const [filters, setFilters] = useState<SuratJalanSearchFilters>({
    status: searchParams.get("status") || "",
    teamOperationsId: searchParams.get("teamOperationsId") || "",
  });
  const [paginationState, setPaginationState] = useState<PaginationParam>({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || DEFAULT_PAGE_SIZE,
  });

  const effectiveFilters = useMemo((): SuratJalanSearchFilters => {
    if (isActionRequired) {
      return { teamOperationsId: filters.teamOperationsId, actionRequired: true };
    }
    return filters;
  }, [filters, isActionRequired]);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
  }, [dispatch]);

  useEffect(() => {
    dispatch(searchSuratJalan({ filters: effectiveFilters, pagination: paginationState }));
  }, [dispatch, effectiveFilters, paginationState]);

  const handleSearch = async (
    newFilters: SuratJalanSearchFilters,
    newPagination: PaginationParam,
  ) => {
    const newParams = new URLSearchParams();
    if (newFilters.status) newParams.set("status", newFilters.status);
    if (newFilters.teamOperationsId) newParams.set("teamOperationsId", newFilters.teamOperationsId);
    newParams.set("page", newPagination.page.toString());
    newParams.set("pageSize", newPagination.pageSize.toString());
    setSearchParams(newParams);
    setFilters(newFilters);
    setPaginationState(newPagination);
  };

  if (error) {
    return (
      <Typography color="error" sx={{ mt: 3 }}>
        {error}
      </Typography>
    );
  }

  const totalCount = pagination?.total ?? suratJalans.length;

  return (
    <Box sx={{ pb: 4 }} data-testid={TestId.SURAT_JALAN.LIST}>
      <DetailPageHeader
        title={
          isActionRequired
            ? t("sidebar.actionRequired" as TranslationKeys)
            : t("Surat Jalan" as TranslationKeys)
        }
        subtitle={
          totalCount > 0
            ? t("suratJalan.listSubtitle" as TranslationKeys, { count: totalCount })
            : t("suratJalan.listSubtitleEmpty" as TranslationKeys)
        }
      />
      {loading && suratJalans.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <SuratJalanTable
          rows={suratJalans}
          teams={operationsTeams}
          filters={filters}
          loading={loading}
          pagination={pagination}
          hideStatusFilter={isActionRequired}
          user={user}
          onActionComplete={() => {
            dispatch(searchSuratJalan({ filters: effectiveFilters, pagination: paginationState }));
          }}
          onFiltersChange={setFilters}
          onSearch={handleSearch}
          onPaginationChange={(next) => {
            const params = new URLSearchParams(searchParams);
            params.set("page", next.page.toString());
            params.set("pageSize", next.pageSize.toString());
            setSearchParams(params);
            setPaginationState(next);
          }}
        />
      )}
    </Box>
  );
};

export default SuratJalanListPage;
